import { useEffect, useState } from 'react';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { Select } from '@components/select';
import { LabeledSwitch } from '@components/switch';
import { Text } from '@components/text';
import { CareerSiteLanguageType, careerSitelanguages } from '@locale/locale-store';
import { useScopedTranslate } from '@locale/translate';

import { useUpdateCompanyInfo } from 'api/companies/hooks';
import { Loading } from 'components/Loading';
import { SettingsSection } from 'components/settings-section';
import { Company } from 'types/companies';

type CareerSocialProps = {
  companyInfo: Company;
};

export function CareerLanguage({ companyInfo }: CareerSocialProps) {
  const { t } = useScopedTranslate(locale);
  const [languageDraft, setLanguageDraft] = useState(companyInfo.defaultLanguage);

  const submit = useUpdateCompanyInfo({
    successMessage: t('updateLangSuccess'),
    errorMessage: t('updateLangError'),
  });

  const companyLanguages = companyInfo.languages.split(',') as CareerSiteLanguageType[];

  useEffect(() => {
    setLanguageDraft(companyInfo.defaultLanguage);
  }, [companyInfo]);

  const addOrRemoveLanguage = ({ lang, addLanguage }: { lang: string; addLanguage: boolean }) => {
    let newList;
    let defaultLanguage = companyInfo.defaultLanguage;
    if (addLanguage) {
      newList = companyInfo.languages.split(',').filter((l) => l);
      newList.push(lang);
    } else {
      newList = companyInfo.languages.split(',').filter((l) => l !== lang);
      if (lang === defaultLanguage) {
        defaultLanguage = newList[0];
      }
    }
    const languages = newList.join(',');
    submit.mutateAsync({ defaultLanguage, languages });
  };

  if (!companyInfo) return <Loading />;

  const isDirty = companyInfo.defaultLanguage !== languageDraft;

  const defaultLanguages = companyLanguages.map((lang) => ({
    value: lang,
    label: t(lang),
  }));

  const hasLanguage = (lang: string) => companyInfo.languages.includes(lang);

  const shouldDisable = (lang: string) =>
    submit.isLoading || (hasLanguage(lang) && defaultLanguages?.length === 1);

  return (
    <SettingsSection
      title={t('languages')}
      description={t('titleHelpText')}
      isBordered
      hasSeparator
    >
      <Text text={t('careerSiteLangHelpText')} font="regular14" />
      {careerSitelanguages.map((lang) => {
        const langText = t(lang);
        return (
          <LabeledSwitch
            key={lang}
            label={langText}
            onText={t('enabled')}
            offText={t('disabled')}
            isDisabled={shouldDisable(lang)}
            defaultValue={hasLanguage(lang)}
            onChange={(value) => {
              addOrRemoveLanguage({ lang, addLanguage: value });
            }}
          />
        );
      })}
      {defaultLanguages.length > 1 && (
        <>
          <Select
            label={t('defaultLang')}
            description={t('defaultLangHelpText')}
            isClearable={false}
            isSearchable={false}
            key={languageDraft + defaultLanguages.length}
            isDisabled={submit.isLoading || defaultLanguages.length === 1}
            defaultValue={languageDraft}
            options={defaultLanguages}
            onChange={setLanguageDraft}
          />
          {isDirty && (
            <CancelSaveButtons
              alwaysShow
              onCancel={() => setLanguageDraft(companyInfo.defaultLanguage)}
              isDisabled={submit.isLoading}
              onSave={() => submit.mutate({ defaultLanguage: languageDraft })}
            />
          )}
        </>
      )}
    </SettingsSection>
  );
}

const locale = {
  en: {
    en: 'English',
    is: 'Icelandic',
    pl: 'Polish',
    no: 'Norwegian',
    sv: 'Swedish',
    fr: 'French',
    languages: 'Languages',
    titleHelpText: "Manage your company's career site languages",
    careerSiteLangHelpText:
      'Your career site will be displayed in the enabled languages and jobs if translations are added (per job)',
    updateLangSuccess: 'Language successfully updated',
    updateLangError: 'Unable to update language. Please contact 50skills support',
    enabled: 'Enabled',
    disabled: 'Disabled',
    defaultLang: 'Primary language',
    defaultLangHelpText:
      "Your career site opens in this language and it's preselected for new jobs. Across the admin, jobs are named and sorted by their title in this language",
  },
  is: {
    en: 'Enska',
    is: 'Íslenska',
    pl: 'Pólska',
    no: 'Norska',
    sv: 'Sænska',
    fr: 'Franska',
    languages: 'Tungumál',
    titleHelpText: 'Stjórnaðu hvaða tungumál eiga við þína ráðningarsíðu',
    careerSiteLangHelpText:
      'Ráðningarsíðan mun bjóða umsækjendum um að skoða störf á mismunandi tungumálum ef þýðing er framkvæmd (per starf)',
    updateLangSuccess: 'Tungumál uppfært',
    updateLangError: 'Ekki var hægt að uppfæra tungumál. Vinsamlegast hafið samband við 50skills',
    enabled: 'Virkt',
    disabled: 'Óvirkt',
    defaultLang: 'Aðaltungumál',
    defaultLangHelpText:
      'Ráðningarsíðan opnast á þessu tungumáli og það er sjálfvalið fyrir ný störf. Í kerfinu eru störf nefnd og raðað eftir titli á þessu tungumáli',
  },
};
